import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for managing a queue of operations with optimistic UI updates.
 * Operations are processed sequentially, one at a time.
 * On failure, the queue is cleared and onError is called.
 *
 * IMPORTANT: Completed operations stay in the queue (marked completed: true)
 * until explicitly cleared via clearCompleted(). This ensures optimistic state
 * persists even if WebSocket brings stale data before server commits.
 *
 * @param {Object} options
 * @param {Function} options.onError - Called with (operation, error) on API failure
 * @param {Function} options.onComplete - Called with (operation) on success (optional)
 * @returns {Object} { pendingOperations, isProcessing, enqueue, clearCompleted }
 */
export function useOperationQueue({ onError, onComplete }) {
    const [pendingOperations, setPendingOperations] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const processingRef = useRef(false);

    // Returns a Promise that resolves when this operation's API call completes
    const enqueue = useCallback((operation) => {
        return new Promise((resolve, reject) => {
            setPendingOperations(prev => [...prev, {
                ...operation,
                id: operation.id || `op-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                completed: false,
                _resolve: resolve,
                _reject: reject
            }]);
        });
    }, []);

    // Clear completed operations (call after verifying server state is correct)
    const clearCompleted = useCallback(() => {
        setPendingOperations(prev => prev.filter(op => !op.completed));
    }, []);

    useEffect(() => {
        // Find first non-completed operation
        const nextOp = pendingOperations.find(op => !op.completed);
        if (processingRef.current || !nextOp) return;

        const processNext = async () => {
            processingRef.current = true;
            setIsProcessing(true);

            try {
                await nextOp.apiCall();
                // Success - mark as completed (don't remove yet!)
                setPendingOperations(prev => prev.map(op =>
                    op.id === nextOp.id ? { ...op, completed: true } : op
                ));
                nextOp._resolve?.();
                onComplete?.(nextOp);
            } catch (error) {
                // Failure - clear queue, reject pending promises
                pendingOperations.forEach(op => op._reject?.(error));
                onError?.(nextOp, error);
                setPendingOperations([]);
            } finally {
                processingRef.current = false;
                setIsProcessing(false);
            }
        };

        processNext();
    }, [pendingOperations, onError, onComplete]);

    return {
        pendingOperations,
        isProcessing,
        enqueue,
        clearCompleted
    };
}
